import { HttpClient } from "aurelia-fetch-client";
import { autoinject } from "aurelia-framework";
import { ApiResponse } from "../models/api-response";
import { Logger } from "../services/logger";

@autoinject
export class RestServiceClient {
    private apiUrl: string;
    jwtToken: string;

    constructor(private httpClient: HttpClient, private logger: Logger) {
        const self = this;
        this.logger = logger;
        this.httpClient = httpClient;
        this.apiUrl = this.getApiAddress();

        httpClient.configure(config => config
            .withBaseUrl(this.apiUrl)
            .withDefaults({
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                }
            })
            .withInterceptor({
                request(request) {
                    console.log(`Requesting ${request.method} ${request.url}`);

                    // add jwt bearer token in request if it exists locally
                    if (self.jwtToken) {
                        if (request.headers.has('Authorization'))
                            request.headers.delete('Authorization');

                        request.headers.append('Authorization', `Bearer ${self.jwtToken}`);
                    }

                    return request;
                },
                response(response) {
                    console.log(`Received ${response.status} ${response.url}`);
                    return response;
                }
            })
        );
    }

    getApiAddress() {
        return `${window.location.protocol}//${window.location.hostname}/HostedPaymentFormApi/`;
    }
    
    get(url: string): Promise<ApiResponse> {
        this.logger.info(`[RestServiceClient] GET. Url: ${url}`);

        return new Promise<ApiResponse>((resolve) => {
            return this.httpClient
                .fetch(url)
                .then(response => {
                    response.json()
                        .then((responseJson: ApiResponse) => {
                            resolve(this.createApiResponseFromHttpResponse(responseJson));
                        })
                        .catch(error => {
							this.logger.error(error.Message, error);
							resolve(new ApiResponse({
								isSuccessful: false,
								errors: ['API Get Failed.']
							}));
                        });
                })
                .catch(error => {
					this.logger.error(error.Message, error);
					resolve(new ApiResponse({
						isSuccessful: false,
						errors: ['API Get Failed.']
					}));
                });
        });
    }

    post(url: string, content: any): Promise<ApiResponse> {
        let request = JSON.stringify(content);

        let init = {
            method: 'post',
            body: request
        };

        this.logger.info(`[RestServiceClient] POST. Url: ${url}. Body: ${request}`);

        return new Promise<ApiResponse>((resolve) => {
            return this.httpClient
                .fetch(url, init)
                .then(response => {
                    response.json()
                        .then((responseJson: ApiResponse) => {
                            resolve(this.createApiResponseFromHttpResponse(responseJson));
                        })
                        .catch(error => {
							this.logger.error(error.Message, error);
							resolve(new ApiResponse({
								isSuccessful: false,
								errors: ['API Post Failed.']
							}));
                        });
                })
                .catch(error => {
					resolve(new ApiResponse({
						isSuccessful: false,
						errors: ['API Post Failed.']
					}));
                });
        });
    }

    put(url: string, content: any): Promise<ApiResponse> {
        let request = JSON.stringify(content);

        let init = {
            method: 'put',
            body: request
        };
        
        this.logger.info(`[RestServiceClient] PUT. Url: ${url}. Body: ${request}`);

        return new Promise<ApiResponse>((resolve) => {
            this.httpClient
                .fetch(url, init)
                .then(response => {
                    response.json()
                        .then((responseJson: ApiResponse) => {
                            resolve(this.createApiResponseFromHttpResponse(responseJson));
                        })
                        .catch(error => {
							this.logger.error(error.Message, error);
							resolve(new ApiResponse({
								isSuccessful: false,
								errors: ['API Put Failed.']
							}));
                        });
                })
                .catch(error => {
					this.logger.error(error.Message, error);
					resolve(new ApiResponse({
						isSuccessful: false,
						errors: ['API Put Failed.']
					}));
                });
        });        
    }

    delete(url: string, content: any): Promise<ApiResponse> {
        let request = JSON.stringify(content);

        let init = {
            method: 'delete',
            body: request
        };

        this.logger.info(`[RestServiceClient] DELETE. Url: ${url}. Body: ${request}`);

        return new Promise<ApiResponse>((resolve) => {
            return this.httpClient
                .fetch(url, init)
                .then(response => {
                    response.json()
                        .then((responseJson: ApiResponse) => {
                            resolve(this.createApiResponseFromHttpResponse(responseJson));
                        })
                        .catch(error => {
							this.logger.error(error.Message, error);
							resolve(new ApiResponse({
								isSuccessful: false,
								errors: ['API Delete Failed.']
							}));
                        });
                })
                .catch(error => {
					this.logger.error(error.Message, error);
					resolve(new ApiResponse({
						isSuccessful: false,
						errors: ['API Delete Failed.']
					}));
                });
        });
    }

    // explicitly map the http json response into an api response in order to overcome 
    // serialization issues that come with implicit serialization (e.g. methods undefined)
    createApiResponseFromHttpResponse(responseJson: ApiResponse): ApiResponse {
        return new ApiResponse({
            isSuccessful: responseJson.isSuccessful,
            resultCode: responseJson.resultCode,
            value: responseJson.value,
            logMessages: responseJson.logMessages,
            errors: responseJson.errors
        });
    }
}